import { type ChangeEvent, type ChangeEventHandler, type DragEvent, useRef, useState } from 'react'

import { CircularProgress } from '@mui/material'
import { Icon } from 'components'
import { theme } from 'styles/theme/hav-theme'

import * as S from './styles'

type OnChangeUpload = ChangeEventHandler<HTMLInputElement>

export interface UploadAreaProps {
  id?: string
  name?: string
  accept?: string
  disabled?: boolean
  required?: boolean
  loading?: boolean
  loadingProgress?: number
  uploadAreaText?: string | React.ReactElement
  onChange: OnChangeUpload
}

export const UploadArea: React.FC<UploadAreaProps> = ({
  name = undefined,
  id,
  accept = '',
  onChange,
  required = false,
  disabled = false,
  loading = false,
  loadingProgress,
  uploadAreaText,
}) => {
  const InputRef = useRef<HTMLInputElement>(null)
  const [dragActive, setDragActive] = useState(false)

  const handleDrag = function (e: DragEvent<HTMLDivElement>) {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const handleDrop = function (e: DragEvent<HTMLDivElement>) {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      if (InputRef.current) {
        const changeEvent = new Event('change', { bubbles: true })
        InputRef.current.files = e.dataTransfer.files
        InputRef.current.dispatchEvent(changeEvent)
      }
    }
  }

  const handleOnchange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e) {
      onChange(e)
    }
  }

  return (
    <S.UploadArea
      isDragActive={dragActive}
      loading={loading ? 'true' : 'false'}
      onDragEnter={handleDrag}
    >
      <input
        accept={accept}
        disabled={disabled}
        id={id}
        name={name}
        onChange={handleOnchange}
        ref={InputRef}
        required={required}
        type="file"
      />
      <label htmlFor={id} id={id}>
        <S.UploadAreaContent>
          <S.IconWrapper>
            <Icon color={theme.colors.neutral.high} icon="empty" size="44" />
          </S.IconWrapper>
          <S.TextContainer>
            <S.Title>O pedido está vazio</S.Title>
            {uploadAreaText && <S.Text disabled={disabled}>{uploadAreaText}</S.Text>}
          </S.TextContainer>
        </S.UploadAreaContent>
      </label>
      {dragActive && (
        <S.DragOverlay
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <S.OverlayContent>
            <S.IconWrapper>
              <Icon icon="upload" size="42" />
            </S.IconWrapper>
            <S.Title>Solte o arquivo aqui</S.Title>
          </S.OverlayContent>
        </S.DragOverlay>
      )}
      {loading && (
        <S.LoadingOverlay>
          <S.LoadingAreaContent>
            <S.IconWrapper>
              <CircularProgress />
            </S.IconWrapper>
            <S.TextContainer>
              <S.Title>Carregando...</S.Title>
              {!!loadingProgress && (
                <S.LoadingBar>
                  <S.LoadingFill progress={loadingProgress} />
                </S.LoadingBar>
              )}
            </S.TextContainer>
          </S.LoadingAreaContent>
        </S.LoadingOverlay>
      )}
    </S.UploadArea>
  )
}
